/**
 * Admin workspace detail page for /admin/workspaces/:id route.
 * Provides tabbed interface for managing a specific workspace.
 */

import { useState } from "react";
import { useParams, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../components/layout/PageHeader";
import { useAdminWorkspaceDetail } from "../hooks/useAdminWorkspaces";
import { useProfile } from "../contexts/ProfileContext";
import { WorkspaceSettingsForm } from "./admin/WorkspaceSettingsForm";
import { ProjectTemplateAssignment } from "./admin/ProjectTemplateAssignment";
import { AdminCollaboratorsList } from "./admin/AdminCollaboratorsList";
import { WorkspaceProjectsList } from "./admin/WorkspaceProjectsList";
import { WorkspaceBoostUsage } from "./admin/WorkspaceBoostUsage";
import { DangerZone } from "./admin/DangerZone";
import {
  AlertCircle,
  Settings,
  FileText,
  Users,
  FolderKanban,
  Zap,
  AlertTriangle,
  ArrowLeft,
} from "lucide-react";
import { publicRoutes } from "../lib/routes";

type TabId = "settings" | "templates" | "collaborators" | "projects" | "usage" | "danger";

interface Tab {
  id: TabId;
  labelKey: string;
  icon: typeof Settings;
}

const tabConfig: Tab[] = [
  { id: "settings", labelKey: "adminWorkspaces.detail.tabs.settings", icon: Settings },
  { id: "templates", labelKey: "adminWorkspaces.detail.tabs.templates", icon: FileText },
  { id: "collaborators", labelKey: "adminWorkspaces.detail.tabs.collaborators", icon: Users },
  { id: "projects", labelKey: "adminWorkspaces.detail.tabs.projects", icon: FolderKanban },
  { id: "usage", labelKey: "adminWorkspaces.detail.tabs.usage", icon: Zap },
  { id: "danger", labelKey: "adminWorkspaces.detail.tabs.dangerZone", icon: AlertTriangle },
];

export function AdminWorkspaceDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin, isLoading: isProfileLoading } = useProfile();
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get("tab") as TabId) || "settings";
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);

  // Redirect non-admins to dashboard
  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={publicRoutes.home} replace />;
  }

  const { data: workspace, isLoading, error } = useAdminWorkspaceDetail(id);

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title={t("adminWorkspaces.detail.loading")}
          isAdmin
          breadcrumbs={[
            { label: t("adminWorkspaces.breadcrumbs.dashboard"), href: publicRoutes.home },
            { label: t("adminWorkspaces.breadcrumbs.admin") },
            { label: t("adminWorkspaces.breadcrumbs.workspaces"), href: "/admin/workspaces" },
            { label: "..." },
          ]}
        />
        <div className="space-y-4">
          <div className="h-12 bg-muted rounded-lg animate-pulse" />
          <div className="h-64 bg-muted rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader
          title={t("adminWorkspaces.detail.error")}
          isAdmin
          breadcrumbs={[
            { label: t("adminWorkspaces.breadcrumbs.dashboard"), href: publicRoutes.home },
            { label: t("adminWorkspaces.breadcrumbs.admin") },
            { label: t("adminWorkspaces.breadcrumbs.workspaces"), href: "/admin/workspaces" },
            { label: t("adminWorkspaces.detail.error") },
          ]}
        />
        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800 dark:text-red-200">{t("adminWorkspaces.detail.loadError")}</p>
            <p className="text-xs text-red-600">{error.message}</p>
          </div>
          <button
            onClick={() => navigate("/admin/workspaces")}
            className="px-3 py-1.5 text-sm text-red-700 dark:text-red-300 border border-red-300 rounded-lg hover:bg-red-100 transition-colors"
          >
            {t("adminWorkspaces.detail.backToWorkspaces")}
          </button>
        </div>
      </div>
    );
  }

  if (!workspace) {
    return <Navigate to="/admin/workspaces" replace />;
  }

  const getStatusBadge = () => {
    if (workspace.deleted_at) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-foreground">
          {t("adminWorkspaces.status.deleted")}
        </span>
      );
    }
    if (workspace.is_blocked) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300">
          {t("adminWorkspaces.status.blocked")}
        </span>
      );
    }
    return null;
  };

  return (
    <div>
      <PageHeader
        title={workspace.name}
        description={workspace.handle ? `@${workspace.handle}` : undefined}
        isAdmin
        breadcrumbs={[
          { label: t("adminWorkspaces.breadcrumbs.dashboard"), href: publicRoutes.home },
          { label: t("adminWorkspaces.breadcrumbs.admin") },
          { label: t("adminWorkspaces.breadcrumbs.workspaces"), href: "/admin/workspaces" },
          { label: workspace.name },
        ]}
        actions={
          <div className="flex items-center gap-2">
            {getStatusBadge()}
            <button
              onClick={() => navigate("/admin/workspaces")}
              className="inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("adminWorkspaces.detail.back")}
            </button>
          </div>
        }
      />

      {/* Tabs */}
      <div className="mb-6">
        {/* Mobile: Dropdown */}
        <div className="sm:hidden">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value as TabId)}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {tabConfig.map((tab) => (
              <option key={tab.id} value={tab.id}>
                {t(tab.labelKey)}
              </option>
            ))}
          </select>
        </div>

        {/* Desktop: Tab bar */}
        <div className="hidden sm:block border-b border-border">
          <nav className="flex space-x-4" aria-label="Tabs">
            {tabConfig.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                    isActive
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                  } ${tab.id === "danger" ? "text-red-600 hover:text-red-700 dark:text-red-300" : ""}`}
                >
                  <Icon className={`h-4 w-4 ${tab.id === "danger" && !isActive ? "text-red-500" : ""}`} />
                  {t(tab.labelKey)}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-card rounded-lg border border-border p-6">
        {activeTab === "settings" && (
          <WorkspaceSettingsForm workspace={workspace} />
        )}
        {activeTab === "templates" && (
          <ProjectTemplateAssignment workspaceId={workspace.id} />
        )}
        {activeTab === "collaborators" && (
          <AdminCollaboratorsList workspaceId={workspace.id} />
        )}
        {activeTab === "projects" && (
          <WorkspaceProjectsList workspaceId={workspace.id} />
        )}
        {activeTab === "usage" && (
          <WorkspaceBoostUsage workspaceId={workspace.id} />
        )}
        {activeTab === "danger" && (
          <DangerZone workspace={workspace} />
        )}
      </div>
    </div>
  );
}
